/**
 * SOLID Review
 * S — Strong. One job: a per-tab, discard-durable key/value store built on window.name (tab id) +
 *     namespaced localStorage, with a TTL-based prune sweep. The header comment lays out the exact problem
 *     (sessionStorage lost on mobile tab discard vs localStorage shared across tabs) and the module does
 *     only that.
 * O — Good. TTL_MS and the id-generation strategy (crypto.randomUUID with a fallback) are the only
 *     variation points; the getItem/setItem/removeItem shape mirrors the Web Storage API so swapping the
 *     underlying mechanism wouldn't require call-site changes.
 * L — N/A — no inheritance hierarchy; a plain object literal with three methods.
 * I — Strong. Minimal three-method surface (getItem/setItem/removeItem) matching what consumers actually
 *     need.
 * D — Strong. Zero imports: a standalone wrapper over window.name and localStorage.
 *
 *     RELOCATED infrastructure/storage/ -> shared/ on 2026-08-17. This header used to record the
 *     direct imports from gameStore.ts, authStore.ts and apolloClient.ts as "documented,
 *     intentional technical debt from module-scope DI limitations" — three baselined
 *     dependency-cruiser violations, two of them against a rule set to `error`. The debt was
 *     illusory. A module with no imports, wrapping two browser primitives, with no swappable
 *     production variant and no async setup, is not a providing adapter; it is shared plumbing, and
 *     filing it under infrastructure/ was the actual error. Moving it makes every one of those
 *     imports an ordinary inward dependency and closes the violations without inventing a port
 *     that would have wrapped a synchronous three-method surface in indirection nobody needed.
 *
 *     Note for the next reader: "add an abstraction" was the obvious fix and the wrong one. The
 *     question worth asking of a layering violation first is whether the file is in the right
 *     layer.
 */
// Per-tab, discard-durable storage.
//
// Problem it solves: identity (the registered-user JWT) was kept in
// `sessionStorage` for PER-TAB account isolation (two tabs of one browser can be
// two different accounts — see §13.11). But mobile browsers DISCARD backgrounded
// tabs under memory pressure and reload them cold, which wipes `sessionStorage`,
// signing the player out. `localStorage` survives a discard but is SHARED across
// all tabs, which would collapse the two accounts into one.
//
// This gives both: a per-tab id is kept in `window.name` (which persists across
// reloads AND across an OS discard→restore of the same tab), and the actual data
// lives in `localStorage` namespaced by that id. So each tab reads only its own
// slice, and the slice survives a discard.
//
// Security: ONLY the opaque random tab id goes in `window.name` — never the JWT.
// `window.name` is readable by any page the tab later navigates to (including
// cross-origin), whereas `localStorage` is origin-scoped. The id is non-sensitive.
//
// Limit: surviving a FULL tab close (window object destroyed) is impossible for
// any per-tab scheme — `window.name` is gone — and the player must log in again.
// This only targets the discard→restore case (the common involuntary one).

const TAB_NAME_PREFIX = 'ml3dtab:';
const TS_SUFFIX = '::__ts';
// Prune a tab's persisted slice this long after its last write, so ids from
// closed tabs don't accumulate in localStorage forever.
const TTL_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

function genId(): string {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch {
    /* fall through */
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

/** The id for this tab, minted into window.name on first use and stable thereafter. */
function tabId(): string {
  try {
    const n = window.name;
    if (n && n.startsWith(TAB_NAME_PREFIX)) return n.slice(TAB_NAME_PREFIX.length);
    const id = genId();
    window.name = TAB_NAME_PREFIX + id;
    return id;
  } catch {
    return 'default';
  }
}

function nsKey(key: string): string {
  return `${key}::${tabId()}`;
}

let _pruned = false;
/** One-shot sweep of stale (closed-tab) slices, by their freshness timestamp. */
function pruneOnce(): void {
  if (_pruned) return;
  _pruned = true;
  try {
    const now = Date.now();
    const remove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      /* c8 ignore next -- localStorage.key(i) cannot return null for i < length */
      if (!k) continue;
      if (!k.endsWith(TS_SUFFIX)) continue;
      const ts = Number(localStorage.getItem(k));
      if (!ts || now - ts > TTL_MS) {
        remove.push(k, k.slice(0, -TS_SUFFIX.length)); // the ::__ts marker AND the data key
      }
    }
    remove.forEach((k) => localStorage.removeItem(k));
  } catch {
    /* ignore */
  }
}

export const tabPersistentStorage = {
  getItem(key: string): string | null {
    pruneOnce();
    try {
      return localStorage.getItem(nsKey(key));
    } catch {
      return null;
    }
  },
  setItem(key: string, value: string): void {
    pruneOnce();
    try {
      const k = nsKey(key);
      localStorage.setItem(k, value);
      localStorage.setItem(k + TS_SUFFIX, String(Date.now()));
    } catch {
      /* ignore */
    }
  },
  removeItem(key: string): void {
    try {
      const k = nsKey(key);
      localStorage.removeItem(k);
      localStorage.removeItem(k + TS_SUFFIX);
    } catch {
      /* ignore */
    }
  },
};
